import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { formatOrderNumberLabel } from '../lib/orderUtils';
import { formatOrderPickup } from '../lib/orderStatus';

const STATUS_OPTIONS = [
  { id: 'open', label: 'Open' },
  { id: 'acknowledged', label: 'Acknowledged' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Dismissed' },
];

export default function ComplaintsPanel({
  complaints = [],
  onUpdate,
  showTruck = false,
  emptyText = 'No late-order complaints.',
  testId = 'complaints-panel',
}) {
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const draftFor = (complaint) => drafts[complaint.complaint_id] || {
    status: complaint.status || 'open',
    response: complaint.response || '',
  };

  const setDraft = (complaint, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [complaint.complaint_id]: { ...draftFor(complaint), ...patch },
    }));
  };

  const save = async (complaint) => {
    if (!onUpdate) return;
    setSavingId(complaint.complaint_id);
    try {
      await onUpdate(complaint, draftFor(complaint));
    } finally {
      setSavingId(null);
    }
  };

  if (!complaints.length) {
    return (
      <div className="bg-card rounded-xl border p-6 text-center text-muted-foreground" data-testid={testId}>
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={testId}>
      {complaints.map((complaint) => {
        const draft = draftFor(complaint);
        return (
          <article key={complaint.complaint_id} className="bg-card rounded-xl border p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">
                  Order {formatOrderNumberLabel(complaint)} · {complaint.student_name || 'Student'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Pickup: {formatOrderPickup(complaint.scheduled_pickup_time)}
                  {showTruck && complaint.truck_name ? ` · ${complaint.truck_name}` : ''}
                </p>
              </div>
              <Badge variant={complaint.status === 'open' ? 'destructive' : 'secondary'}>
                {STATUS_OPTIONS.find((s) => s.id === complaint.status)?.label || complaint.status}
              </Badge>
            </div>
            <p className="text-sm bg-muted/60 rounded-lg p-3">{complaint.message}</p>
            <div className="grid gap-2 sm:grid-cols-[180px_1fr_auto]">
              <select
                value={draft.status}
                onChange={(e) => setDraft(complaint, { status: e.target.value })}
                className="rounded-lg bg-muted border-0 px-3 py-2 text-sm"
                data-testid={`complaint-status-${complaint.complaint_id}`}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
              <input
                value={draft.response}
                onChange={(e) => setDraft(complaint, { response: e.target.value })}
                placeholder="Response visible to the student"
                maxLength={1000}
                className="rounded-lg bg-muted border-0 px-3 py-2 text-sm"
                data-testid={`complaint-response-${complaint.complaint_id}`}
              />
              <Button
                type="button"
                size="sm"
                onClick={() => save(complaint)}
                disabled={savingId === complaint.complaint_id}
                data-testid={`complaint-save-${complaint.complaint_id}`}
              >
                {savingId === complaint.complaint_id ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
